import { Component, OnDestroy, OnInit } from '@angular/core'
import { NavBarComponent } from '../../layout/components/nav-bar/nav-bar.component'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { ProjectService } from '../../services/project.service'
import { htmlToPlainText, LogService, ProjectCMSButtonActionType, ProjectCMSMenu, ScreenResolution } from 'htv-lib'
import { Subscription } from 'rxjs'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { MediaService } from '../../services/media.service'
import { StateService } from '../../services/state.service'
import { ResolutionService } from '../../services/resolution.service'
import { LanguageService } from '../../services/language.service'

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [NavBarComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class HomeComponent implements OnInit, OnDestroy {
  activatedRouteSubscription?: Subscription
  projectLoadedSubscription?: Subscription
  resolutionSubscription?: Subscription

  pageHtml: SafeHtml = ''
  currentPageId?: string
  projectId?: string
  menus: ProjectCMSMenu[] = []
  currentResolution: ScreenResolution = ScreenResolution.RES_1080P

  projectLoaded = false

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private activatedRoute: ActivatedRoute,
    private logService: LogService,
    private projectService: ProjectService,
    private mediaService: MediaService,
    private resolutionService: ResolutionService,
    private stateService: StateService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private languageService: LanguageService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.projectLoadedSubscription = this.stateService.isProjectLoaded$.subscribe(isLoaded => {
      this.projectLoaded = isLoaded
      if (this.projectLoaded) {
        this.projectId = this.projectService.getProjectId()
        this.handlePageId()
      }
    })
    this.activatedRouteSubscription = this.activatedRoute.params.subscribe(() => {
      if (this.projectLoaded) {
        this.handlePageId()
      }
    })
    this.resolutionSubscription = this.resolutionService.resolutionChanged$.subscribe(resolution => {
      this.currentResolution = resolution
      if (this.projectLoaded) {
        this.handlePageId()
      }
    })
  }

  ngOnDestroy(): void {
    this.activatedRouteSubscription?.unsubscribe()
    this.projectLoadedSubscription?.unsubscribe()
    this.resolutionSubscription?.unsubscribe()
    this.stopAllVideoSources()
  }

  handlePageId() {
    const pageId = this.activatedRoute.snapshot.params['pageId']
    if (pageId) {
      this.loadPage(pageId)
    } else {
      this.loadPage(this.projectService.getProjectPageStartId())
    }
  }

  loadPage(pageId: string): void {
    this.stopAllVideoSources()

    const page = this.projectService.getPageById(pageId)
    if (!page) {
      this.logService.error(`[HomeComponent] Could not find page with id = "${pageId}"`)
      return
    }

    this.currentPageId = page.id

    const translations = this.projectService.getProjectTranslations()
    const htmlContent = page.htmlContent

    let translatedHtml = this.translateHtmlContent(htmlContent, translations)
    translatedHtml = this.processVideosInHtml(translatedHtml)
    translatedHtml = this.translateMediaSources(translatedHtml, translations)
    translatedHtml = this.translateVideoSources(translatedHtml, translations)

    this.pageHtml = this.sanitizer.bypassSecurityTrustHtml(translatedHtml)

    this.menus = page.menus
      .map(menu => {
        menu.label = htmlToPlainText(translations[menu.labelTranslationId])
        if (menu.predefinedImage) {
          menu.mediaSrc = `assets/images/menus/${menu.predefinedImage}.png`
        } else if (menu.actionType !== ProjectCMSButtonActionType.SET_LANGUAGE) {
          menu.mediaSrc = this.mediaService.getMediaUrl(translations[menu.mediaTranslationId])
        } else {
          menu.mediaSrc = `assets/images/flags/${menu.actionLanguageCode}.svg`
        }
        return menu
      })
      .sort((a, b) => a.position - b.position)

    this.appButtonDirective.forceRefreshSelectedButton()
  }

  onNavButtonClick(navButton: ProjectCMSMenu): void {
    switch (navButton.actionType) {
      case ProjectCMSButtonActionType.PAGE_NAVIGATION:
        if (navButton.actionNavigationPageId) {
          this.router.navigate(['home', this.projectId, 'page', navButton.actionNavigationPageId], {
            queryParamsHandling: 'preserve',
          })
        }
        break
      case ProjectCMSButtonActionType.SET_LANGUAGE:
        if (navButton.actionLanguageCode) {
          this.languageService.setSelectedLanguageFromCode(navButton.actionLanguageCode)
        }
        break
      case ProjectCMSButtonActionType.OPEN_URL:
        this.logService.log(`url : ${navButton.actionNavigationUrl}`)

        if (navButton.actionNavigationUrl?.includes('genesis')) {
          this.logService.log('open genesis')
          window.parent.location.href = 'http://hes.genesis/tv/menu.do'
          return
        }
        break
    }
  }

  translateHtmlContent(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    const textNodes = doc.querySelectorAll('[data-text-id]')

    if (textNodes.length === 0) {
      return htmlContent.replace(/i18n-[^\s<]+/g, key => translations[key] || key)
    }

    textNodes.forEach(node => {
      const translation = translations[`i18n-${node.getAttribute('data-text-id')}`]
      if (translation === undefined) {
        return
      }
      const scopedStyle = node.firstElementChild?.tagName === 'STYLE' ? node.firstElementChild : null
      node.innerHTML = translation
      if (scopedStyle) {
        node.insertBefore(scopedStyle, node.firstChild)
      }
    })

    return doc.body.innerHTML
  }

  translateMediaSources(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    doc.querySelectorAll('div[data-i18n-src]').forEach(div => {
      const translationKey = div.getAttribute('data-i18n-src') || ''
      const media = this.mediaService.getMedia(translations[translationKey])

      if (media && media.url) {
        const style = div.getAttribute('style') || ''
        const translatedUrl = this.mediaService.getMediaUrl(translations[translationKey]) ?? 'assets/images/logo.svg'

        let newStyle = undefined
        if (style.includes(`background-image: url`)) {
          newStyle = style.replace(/background-image: url\([^)]+\)/, `background-image: url(${translatedUrl})`)
        } else if (style.includes(`background:url`)) {
          newStyle = style.replace(/background:url\([^)]+\)/, `background:url(${translatedUrl})`)
        } else if (style.includes(`background: url`)) {
          newStyle = style.replace(/background: url\([^)]+\)/, `background: url(${translatedUrl})`)
        }

        if (newStyle) {
          div.setAttribute('style', newStyle)
        }
      }
    })
    return doc.body.innerHTML
  }

  translateVideoSources(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    doc.querySelectorAll('video').forEach(video => {
      const translationKey = video.getAttribute('data-i18n-src') || ''
      const translatedUrl = this.mediaService.getMediaUrl(translations[translationKey]) ?? video.getAttribute('src')
      if (translatedUrl) {
        video.setAttribute('src', translatedUrl)
        video.load()
        video.addEventListener('canplay', () => {
          video.play().catch(error => {
            this.logService.error(`Error during video playback : ${error}`)
          })
        })
        video.addEventListener('error', () => {
          const mediaError = video.error
          if (mediaError) {
            let errorMessage = ''
            switch (mediaError.code) {
              case mediaError.MEDIA_ERR_ABORTED:
                errorMessage = 'Media recovery cancelled by user.'
                break
              case mediaError.MEDIA_ERR_NETWORK:
                errorMessage = 'Network error - media could not be recovered.'
                break
              case mediaError.MEDIA_ERR_DECODE:
                errorMessage = 'Error decoding media.'
                break
              case mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorMessage = 'Media not supported.'
                break
              default:
                errorMessage = 'Unknown error.'
            }
            this.logService.error(`Error loading video from ${video.src} : code ${mediaError.code} - ${errorMessage}`)
          } else {
            this.logService.error(`Error loading video from ${video.src}: no error details available.`)
          }
        })
      } else {
        video.remove()
      }
    })
    return doc.body.innerHTML
  }

  divMatchesText(element: HTMLElement, targetText: string): boolean {
    return Array.from(element.childNodes).some(
      node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim().includes(targetText)
    )
  }

  processVideosInHtml(htmlContent: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    doc.querySelectorAll('video').forEach(video => {
      video.removeAttribute('controls')
      video.removeAttribute('volume')
      video.removeAttribute('playsInline')
      video.removeAttribute('muted')

      video.setAttribute('preload', 'auto')
      video.setAttribute('autoplay', '')
      video.setAttribute('type', 'video/mp4')
      video.setAttribute('loop', '')

      const audioEnabled = video.getAttribute('data-audio-enabled') === 'true'
      if (!audioEnabled) {
        video.setAttribute('muted', '')
      }
    })

    return doc.body.innerHTML
  }

  stopAllVideoSources() {
    document.querySelectorAll('video').forEach(video => {
      video.pause()
      video.src = ''
      video.removeAttribute('src')
      video.remove()
    })
  }
}
